import dedent from 'ts-dedent'
import {
  GrantsAndAllowedApis,
  Provider,
  QueryAndOutputs,
  TokenAllowedApis,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'
import { WorkflowComponent } from './common/workflowcomponent'

const formats = ['CSV', 'JSON']
const options: Record<string, string> = {
  CSV: `compression = 'GZIP',
        header = true,
        field_delimiter = ','`,
  JSON: "compression = 'GZIP'"
}
const extensions: Record<string, string> = {
  CSV: 'csv',
  JSON: 'json'
}
class SaveToBucketAndNotify extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'SaveToBucketAndNotify',
        group: 'Input/Output',
        title: 'Send by Email',
        description: `
      This component sends an email with a custom subject and body. Optionally, it can export data to a bucket and add the resulting link(s) to the email's body.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          optional: true
        },
        {
          name: 'email',
          type: WorkflowComponentParamType.Email,
          title: 'Email addresses',
          description:
            'single email string or JSON-formatted array of email addresses',
          placeholder:
            'E.g.: user@example.com or ["user1@example.com", "user2@example.com"]',
          allowExpressions: true
        },
        {
          name: 'subject',
          type: WorkflowComponentParamType.String,
          title: 'Subject',
          placeholder: 'E.g.: CARTO Workflows result',
          allowExpressions: false
        },
        {
          name: 'body',
          type: WorkflowComponentParamType.String,
          mode: WorkflowComponentParameterMode.Multiline,
          title: 'Body',
          optional: true,
          default: '',
          placeholder: 'Hello, this is an example body for your email',
          allowExpressions: false
        },
        {
          name: 'include_data',
          type: WorkflowComponentParamType.Boolean,
          title: 'Include data',
          default: false
        },
        {
          name: 'format',
          type: WorkflowComponentParamType.Selection,
          title: 'File format',
          default: formats[0],
          options: formats,
          showIf: [
            {
              parameter: 'include_data',
              value: true
            }
          ]
        },
        {
          name: 'bucket',
          type: WorkflowComponentParamType.String,
          title: 'GCS Bucket name',
          placeholder: 'Bucket name',
          optional: true,
          showIf: [
            {
              parameter: 'include_data',
              value: true
            }
          ]
        }
      ],
      []
    )
    this.supportedProviders = [Provider.BigQuery]
  }

  parseEmails(value: unknown) {
    try {
      return JSON.parse(value as string)
    } catch {
      return [value]
    }
  }

  getVersion(provider: Provider): string {
    return '2'
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    if (connection.type !== Provider.BigQuery) {
      return null
    }
    const dryRun = codeType === WorkflowCodeType.DryRun
    const preparedParams = this._prepareParameters(params)

    let query = ''
    if (!dryRun) {
      let body = ((preparedParams.body as string) ?? '')
        .replace(/\n/g, '<br>')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
      let emails = ''
      if (
        isExpressionValue(
          workflow.componentValues[componentName]
            .email as WorkflowComponentValue
        )
      ) {
        emails = `CONCAT('["', ${preparedParams.email}, '"]')`
      } else {
        emails = `'''${JSON.stringify(
          this.parseEmails(preparedParams.email)
        )}'''`
      }
      if (preparedParams.include_data) {
        const projectId = envVars.bigqueryProjectId
        const regionId = `region-${envVars.bigqueryRegion}`
        const format = params.format as string
        const extension = extensions[format]
        const defaultBucket =
          envVars.dataExportDefaultGCSBucket ??
          'carto-tnt-gcp-us-east1-export-storage'
        const bucket = preparedParams.bucket ?? defaultBucket
        body =
          body +
          '<br><ul>{% for bucketUrl in bucketUrls %}<li>{{ bucketUrl }}</li>{% endfor %}</ul>'

        query = dedent`
        DECLARE emails STRING DEFAULT ${emails};
        DECLARE export_id STRING DEFAULT GENERATE_UUID();
        DECLARE bucket STRING DEFAULT '${bucket}';
        DECLARE response STRING;
        DECLARE headers STRING DEFAULT '''
        { "Authorization": "Bearer ${envVars.accessToken}",
          "Content-Type": "application/json"
        }''';
        DECLARE bucket_urls STRING;
        DECLARE payload STRING;
        EXECUTE IMMEDIATE FORMAT('''
          EXPORT DATA -- carto-export-id: %s
            OPTIONS (
              uri = 'gs://%s/%s-*.${extension}.gz',
              format = '${format}',
              overwrite = true,
              ${options[format]}
          )
          AS (
            SELECT * FROM \`${preparedParams.source}\`
          )
         ''',
         export_id, bucket, export_id
         );
        SET bucket_urls = (
          WITH export_result AS (
            SELECT timeline[0].pending_units + timeline[0].completed_units AS num_files
            FROM \`${projectId}\`.\`${regionId}\`.INFORMATION_SCHEMA.JOBS_BY_USER
            WHERE creation_time > TIMESTAMP_ADD(CURRENT_TIMESTAMP(), INTERVAL -1 HOUR)
              AND statement_type = 'EXPORT_DATA'
              AND query LIKE CONCAT('%', '-- carto-export-id: ', export_id, '%')
            LIMIT 1
          )
          SELECT ARRAY_TO_STRING(ARRAY(
            SELECT CONCAT(
              '"',
              'https://storage.googleapis.com/',
              bucket,
              '/',
              export_id,
              '-',
              LPAD(CAST(i AS STRING), 12, '0'),
              '.',
              '${extension}',
              '.gz',
              '"'
            ) AS url
            FROM export_result, UNNEST(GENERATE_ARRAY(0, num_files - 1)) AS i
            ORDER BY i), ', ')
        );
        SET payload = CONCAT('''
          {
            "method": "email",
            "payload": {
              "emails": ''', emails, ''',
              "subject": "${preparedParams.subject}",
              "body": "${body}",
              "replacements": {
                "bucketUrls": [ ''', bucket_urls, ''']
              }
            }
          }
          '''
        );
        SET response = \`${envVars.analyticsToolboxDataset}.__REQUEST\`(
          'post', '${envVars.apiBaseUrl}',
          'v3/workflows/notify', payload, headers
        );
`
      } else {
        if (body === '') {
          body = 'This is an email generated by CARTO Workflows'
        }
        query = dedent`
        DECLARE emails STRING DEFAULT ${emails};
        DECLARE response STRING;
        DECLARE headers STRING DEFAULT '''
        { "Authorization": "Bearer ${envVars.accessToken}",
          "Content-Type": "application/json"
        }''';
        DECLARE payload STRING;
        SET payload = CONCAT('''
          {
            "method": "email",
            "payload": {
              "emails": ''', emails, ''',
              "subject": "${preparedParams.subject}",
              "body": "${body}",
              "replacements": {}
            }
          }
          '''
        );
        SET response = \`${envVars.analyticsToolboxDataset}.__REQUEST\`(
          'post', '${envVars.apiBaseUrl}',
          'v3/workflows/notify', payload, headers
        );
`
      }
    }

    // Create a dummy table to be able to retrieve the status of the node.
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    const createTable = connection.getCreateTempTableQuery(
      tablename,
      `SELECT 1 AS N`,
      codeType,
      null
    )
    query += `\n${createTable}`

    return {
      outputs: {},
      query
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    return {
      [SINGLE_OUTPUT_NAME]: tablename
    }
  }

  checkInputValues(
    checkInputValuesParams: WorkflowComponentCheckInputValuesParams
  ) {
    // Do not call super.checkInputValues(), because we do not want to check for wrong variable usage.
    // That can lead to false errors due to emails containing "@"
    const problems: Array<WorkflowParameterValueError> = []
    for (const input of this.inputs) {
      this._checkInputValueIsNotMissing(checkInputValuesParams, input, problems)
      this._checkInputValueTypeAndRange(checkInputValuesParams, input, problems)
    }

    const { resolvedParamValues } = checkInputValuesParams
    if (resolvedParamValues.include_data && !resolvedParamValues.source) {
      problems.push({
        parameter: 'source',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'Source table is required when including data'
      })
    }
    return problems
  }

  getRequiredGrants(): GrantsAndAllowedApis {
    return {
      grants: [],
      apis: [TokenAllowedApis.workflows]
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }
}

export { SaveToBucketAndNotify }
